import { CrmIcon, FlagIcon, UserIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { getPrimaryGuest } from "../../../../models/Guest";
import { RowData } from "./types";
import { canViewPurchase } from "@/stores/globalStore";
import { usePurchaseDrawerStore } from "@/stores/purchaseDrawerStore";

export interface GuestCellProps {
    isOrganizer?: boolean;
    isPrimaryGuest?: boolean;
    purchaseId: ID;
    itemId: ID;
    name?: string;
    guestsCount: number;
    organizer?: ID;
}

export const guestCellValueGetter = ({ data, node }: { data: RowData; node: any }) => {
    if ("balance" in data || !node) {
        return { name: "", guestsCount: 0 };
    }

    // Equipment roster sub-summary rows and equipment unit rows.
    if (data.rosterSection === "attendees") {
        return { name: "Attendees", guestsCount: 0 };
    }
    if (data.rosterSection === "equipment") {
        return { name: "Equipment", guestsCount: 0 };
    }
    if (data.equipment) {
        return { name: data.equipment.name ?? "", guestsCount: 0 };
    }

    const purchaserName = data.organizer?.name ?? "";
    const firstGuestName = data.item.guests?.[0]?.name;
    const primaryName = getPrimaryGuest(data.item.guests)?.name ?? firstGuestName;
    const generatedGuestName = node.hasChildren()
        ? `Guests #1` // parent row as it has children so assigned index 1, cannot use rowIndex as in case of multiple Purchases
        : `Guests #${(node.childIndex as number) + 2}`; // child row so assigned childIndex (starts from 0) + 2
    const guestName = data.isPrimaryGuest ? primaryName : data.guest?.name ?? generatedGuestName;
    return { name: data.isOrganizer ? purchaserName : guestName, guestsCount: data.item.guests?.length ?? 0 };
};

export const GuestCellRenderer = ({
    value,
    data,
}: {
    value?: { name: string; guestsCount: number };
    data: RowData;
}) => {
    if ("balance" in data) {
        return null;
    }

    return (
        <GuestCell
            name={value?.name ?? ""}
            guestsCount={value?.guestsCount ?? 0}
            isOrganizer={data.isOrganizer}
            isPrimaryGuest={data.isPrimaryGuest}
            purchaseId={data.item.purchase.id}
            itemId={data.item.id}
            organizer={data.organizer?.id}
        />
    );
};

const togglePurchaseDrawer = (purchaseId: ID, itemId: ID, organizerId?: ID) => {
    const actions = usePurchaseDrawerStore.getState().actions;
    actions.setPurchaseId(purchaseId);
    actions.setPurchaseItemId(itemId);
    if (organizerId) {
        actions.setContactId(organizerId);
    }
};

export const GuestCell = ({
    name,
    purchaseId,
    itemId,
    isOrganizer,
    guestsCount,
    isPrimaryGuest,
    organizer,
}: GuestCellProps) => {
    const handleTogglePurchaseDrawer = () => {
        if (purchaseId && itemId && organizer) {
            togglePurchaseDrawer(purchaseId, itemId, organizer);
        }
    };

    const isClickable = canViewPurchase();

    return (
        <span className="space-x-2">
            {isOrganizer && guestsCount > 0 && (
                <Badge>
                    <span className="space-x-1">
                        {guestsCount > 1 ? <CrmIcon /> : <UserIcon />}
                        <span>{guestsCount}</span>
                    </span>
                </Badge>
            )}

            <span
                className={clsx("no-translate", isOrganizer && isClickable && "cursor-pointer")}
                data-testid="roster-guests-list-guest-name"
                onClick={isOrganizer && isClickable ? handleTogglePurchaseDrawer : undefined}
            >
                {name}
            </span>

            {isPrimaryGuest && <FlagIcon />}
        </span>
    );
};
